'use client'

import ImgBorderLeftRound from '@/assets/images/border-left-round.svg'
import Link from '@/components/link-locale'
import NoData from '@/components/no-data'
import { siteConfig } from '@/config/site'
import { convertDuration } from '@/lib/utils'
import { Pagination } from '@/types/common'
import { Video } from '@/types/video'
import { formatNumber, getAssetUrl } from '@/utils'
import { LikeOutlined, UserOutlined } from '@ant-design/icons'
import { Avatar, Button } from 'antd'
import useEmblaCarousel from 'embla-carousel-react'
import { ArrowDownCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import React from 'react'

import Loading from './loading'

const VideoPlayer = dynamic(() => import('@/components/video-player'), {
  ssr: false,
})

interface Props {
  isLoading: boolean
  items: Video[]
  pagination?: Pagination
  className?: string
  handleLoadMoreClick?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}

export const VideoThumbnails = ({
  isLoading,
  items,
  className = 'flex items-center px-5 py-6',
  handleLoadMoreClick,
  hasNextPage = false,
  isFetchingNextPage = false,
}: Props) => {
  const t = useTranslations()
  const [emblaRef] = useEmblaCarousel({
    dragFree: true,
  })

  // const handleFavoriteAdd = (
  //   e: React.MouseEvent<HTMLButtonElement>,
  //   post: any,
  // ) => {
  //   e.preventDefault()
  //   e.stopPropagation()
  //   // TODO: Call API
  //   console.log('🚀 ~ handleFavoriteAdd ~ post:', post)
  // }

  // const getDropdownItems = (post: any) => [
  //   {
  //     key: 'unPublish',
  //     label: (
  //       <div className='flex items-center overflow-hidden text-xs font-bold leading-3 text-[#B5B5BD] hover:text-[#FF3C8E]'>
  //         <button onClick={e => handleFavoriteAdd(e, post)}>
  //           {/* <Icon name='Download' size={16} /> */}
  //           <span>{t('component.videoThumbnails.addFavorites')}</span>
  //         </button>
  //       </div>
  //     ),
  //   },
  // ]

  return (
    <Loading isLoading={isLoading}>
      <NoData hasData={!!items && items?.length > 0}>
        <div className='embla hidden lg:block'>
          <div className='embla__viewport' ref={emblaRef}>
            <div className={`embla__container ${className}`}>
              {items?.map((item, index) => {
                const videoJsOptions = {
                  controls: false,
                  autoplay: false,
                  muted: true,
                  sources: [
                    {
                      src: item.previewVideo.url,
                      type: 'application/x-mpegURL',
                    },
                  ],
                }
                return (
                  <Link
                    href={`/video/${item.id}`}
                    key={item.id}
                    className={
                      'embla__slide relative max-w-[320px] rounded-[10px]'
                    }
                  >
                    <div
                      key={`${index}`}
                      className={'relative rounded-[10px] bg-[#333742]'}
                    >
                      <div
                        key={index}
                        className='relative overflow-hidden rounded-[10px] border-2 border-solid border-[#333742]'
                      >
                        <VideoPlayer
                          video={item}
                          options={videoJsOptions}
                          custom={{
                            wrapper:
                              'w-full min-w-[10.5rem] lg:min-w-[315px] aspect-[16/9]',
                          }}
                          playBtnSize={24}
                        />
                        <span className='absolute bottom-1 right-[6.4px] z-20 min-w-[18px] rounded bg-[#59596899] px-2 py-[2px] text-xs font-medium leading-3 text-[#FFFFFF]'>
                          {convertDuration(item.mainVideo?.videoDuration)}
                        </span>
                      </div>
                      <div className='relative w-full rounded-[10px] bg-[#333742]'>
                        <div className='absolute top-[-29px] z-30 flex h-[28px] w-full max-w-[150px] items-end gap-x-2 rounded-tr-xl bg-[#333742] px-[10px]'>
                          <div className='absolute top-[-10px] flex items-end gap-x-2'>
                            <Avatar
                              size={32}
                              icon={<UserOutlined />}
                              src={
                                item.channel?.avatar ||
                                getAssetUrl(siteConfig.placeholder.avatar)
                              }
                              shape='circle'
                              className='!border-1 z-20 min-w-8 !border-solid !border-[#333742]'
                            />
                            <span className='max-w-[90px] truncate text-xs font-bold leading-3 text-[#F9F9F9] hover:underline'>
                              {item.channel?.name}
                            </span>
                          </div>
                          <ImgBorderLeftRound className='pointer-events-none absolute bottom-full left-0 h-4 w-4 fill-[#333743]' />
                          <ImgBorderLeftRound className='pointer-events-none absolute bottom-0 left-full h-4 w-4 fill-[#333743]' />
                        </div>
                        <div className='px-[10px] pb-3 pt-2'>
                          <p
                            className='mb-2 h-[40px] max-h-[72px] w-full overflow-hidden text-ellipsis text-sm font-bold leading-5 text-[#F9F9F9]'
                            style={{
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              display: '-webkit-box',
                            }}
                          >
                            {item.title}
                          </p>
                          <div className='flex items-center justify-between text-[#B5B5BD]'>
                            <span className='flex items-center gap-x-1'>
                              <LikeOutlined className='text-xs' />
                              <span className='text-[10px] font-medium text-[#B5B5BD]'>
                                {formatNumber(item.likes)}
                              </span>
                            </span>
                            <span className='flex items-center gap-x-1 text-[10px] font-medium'>
                              <span>{formatNumber(item.views)}</span>views
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
        {/* NOTE: 手機版 */}
        <div className='grid grid-cols-2 gap-2 lg:hidden'>
          {items?.map((item, index) => {
            // console.log('🚀 ~ {data?.map ~ item:', item)
            const videoJsOptions = {
              controls: false,
              autoplay: false,
              muted: true,
              sources: [
                {
                  src: item.previewVideo.url,
                  type: 'application/x-mpegURL',
                },
              ],
            }
            return (
              <Link
                href={`/video/${item.id}`}
                key={item.id}
                className={'relative rounded-[10px]'}
              >
                <div
                  key={`${index}`}
                  className={'relative rounded-[10px] bg-[#333742]'}
                >
                  <div
                    key={index}
                    className='relative overflow-hidden rounded-[10px] border-2 border-solid border-[#333742]'
                  >
                    <VideoPlayer
                      video={item}
                      options={videoJsOptions}
                      custom={{
                        wrapper: 'w-full h-full aspect-[16/9]',
                      }}
                      playBtnSize={24}
                    />
                    <span className='leading-2 absolute bottom-[7.37px] right-[3.5px] z-20 min-w-[18px] rounded bg-[#59596899] px-[4.75px] text-[8px] font-medium text-[#FFFFFF]'>
                      {convertDuration(item.mainVideo?.videoDuration)}
                    </span>
                  </div>
                  <div className='relative w-full rounded-[10px] bg-[#333742]'>
                    <div className='absolute top-[-24px] z-10 flex h-[24px] w-[calc(100%-56px)] max-w-[150px] items-end gap-x-1 rounded-tr-xl bg-[#333742] px-2'>
                      <div className='absolute top-[-10px] flex w-[calc(100%-10px)] items-end gap-x-1 lg:w-auto'>
                        <Avatar
                          size={28}
                          icon={<UserOutlined />}
                          src={
                            item.channel?.avatar ||
                            getAssetUrl(siteConfig.placeholder.avatar)
                          }
                          shape='circle'
                          className='!border-1 z-20 min-w-7 !border-solid !border-[#333742]'
                        />
                        <span className='leading-2 max-w-[70px] truncate text-[8px] font-bold text-[#F9F9F9] hover:underline'>
                          {item.channel?.name}
                        </span>
                      </div>
                      <ImgBorderLeftRound className='pointer-events-none absolute bottom-full left-0 h-4 w-4 fill-[#333743]' />
                      <ImgBorderLeftRound className='pointer-events-none absolute bottom-0 left-full h-4 w-4 fill-[#333743]' />
                    </div>
                    <div className='px-[10px] pb-2 pt-1'>
                      <p
                        className='mb-1 h-[40px] w-full overflow-hidden text-ellipsis text-xs font-bold leading-5 text-[#F9F9F9]'
                        style={{
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          display: '-webkit-box',
                        }}
                      >
                        {item.title}
                      </p>
                      <div className='flex items-center justify-between text-[#B5B5BD]'>
                        <span className='flex items-center gap-x-1'>
                          <LikeOutlined className='text-xs' />
                          <span className='text-[8px] font-medium text-[#B5B5BD]'>
                            {formatNumber(item.likes)}
                          </span>
                        </span>
                        <span className='flex items-center gap-x-1 text-[8px] font-medium'>
                          <span>{formatNumber(item.views)}</span>views
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
        {hasNextPage && (
          <Button
            className='mt-3 flex h-10 w-full gap-x-[10px] rounded border-none bg-[#1C2127] text-sm font-bold leading-[14px] text-[#808592] lg:hidden'
            onClick={handleLoadMoreClick}
            loading={isFetchingNextPage}
          >
            <ArrowDownCircle size={20} color='#808592' />
            {t('page.home.action.viewMore')}
          </Button>
        )}
      </NoData>
    </Loading>
  )
}
